import axios, { AxiosRequestConfig, CreateAxiosDefaults } from 'axios'
import { useAuthStore, useModalStore, useTokenStore } from '../store'

type CreateAxiosInstanceOptions<T = any> = {
  withAuth?: boolean
  withoutSource?: boolean
} & CreateAxiosDefaults<T>

export function createAxiosInstance(options?: CreateAxiosInstanceOptions) {
  const { withAuth = false, withoutSource = false, ...axiosDefault } = options || {}

  if (!withoutSource) {
    axiosDefault.headers = {
      ...(axiosDefault.headers || {}),
      'x-lang': window.__APP__.SOURCE_LANG,
      'x-source-app': window.__APP__.SOURCE_APP,
      // 'x-api-version': window.__APP__.API_VERSION,
    }
  } else {
    axiosDefault.headers = {
      ...(axiosDefault.headers || {}),
      'x-lang': window.__APP__.SOURCE_LANG,
    }
  }

  let timeZone = 'Asia/Jakarta'

  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    console.error('Error occurred while accessing time zone:', error)
  }

  axiosDefault.headers = {
    ...(axiosDefault.headers || {}),
    'x-timezone': timeZone,
  }

  const request = axios.create(axiosDefault)

  if (withAuth) {
    request.interceptors.request.use(async (config) => {
      if (!config.headers?.Authorization) {
        try {
          const { refreshAccessToken } = useTokenStore.getState()
          const { access_token } = await refreshAccessToken()

          if (access_token) {
            config.headers['Authorization'] = `Bearer ${access_token}`
          }
        } catch {
          //
        }
      }

      const isLocal =
        config?.baseURL?.startsWith('http://192.') ||
        config?.baseURL?.startsWith('http://172.') ||
        config?.baseURL?.startsWith('http://localhost')

      if (isLocal && useAuthStore.getState().user?.company?.oid) {
        config.headers['company_id'] = useAuthStore.getState().user?.company?.oid
      }

      return config
    })

    request.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response && error.response.status === 401) {
          useModalStore.getState().openModal()
          useAuthStore.getState().logout()
          // if (!window.location.pathname.startsWith('/auth')) {
          //   window.location.href = '/auth/login'
          // }
        }
        return Promise.reject(error)
      },
    )
  }

  return request
}

export function axiosErrorMessage(e: any) {
  return e.response?.data?.meta?.message || e.response?.data?.message || e.response?.message || e.message
}

export function mergeParamsWithConfig<
  TParams extends Record<string, any> = Record<string, never>,
  TConfig extends AxiosRequestConfig = AxiosRequestConfig,
>(params?: TParams | null, config?: TConfig | null): AxiosRequestConfig & Pick<TConfig, Exclude<keyof TConfig, keyof AxiosRequestConfig>> {
  const sanitizedParams = params ?? {}
  const sanitizedConfig = config ?? {}

  // Merge the params while preserving types and handling null/undefined
  const mergedParams = {
    // @ts-expect-error
    ...(sanitizedConfig.params ?? {}),
    ...sanitizedParams,
  }

  // Remove undefined values from merged params
  const cleanParams = Object.fromEntries(Object.entries(mergedParams).filter(([_, value]) => value !== undefined))

  return {
    ...sanitizedConfig,
    params: cleanParams,
  } as AxiosRequestConfig & Pick<TConfig, Exclude<keyof TConfig, keyof AxiosRequestConfig>>
}
